import { Grid, makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import React from 'react';
import { Trash } from 'react-feather';
import { TFunction } from 'react-i18next';
import { usePrefixTranslation } from '../../../../i18n';
import { actions, useTypedDispatch, useTypedSelector } from '../../../../redux';
import { theme } from '../../../../theme/theme';
import { Asset } from '../../../../types/asset';
import { enumToStatusType, inspectionTypesCombined, probabilities } from '../../../../types/common';
import { DamageMechanism, Plan } from '../../../../types/plan';
import { getContentUrl } from '../../../../util/file';
import { Badge, DisplayPicture, IconButton, Select } from '../../../global';
import { PictureUploader } from '../../../global/PictureUploader';
import { SearchBar } from '../../../global/SearchBar';
import { SectionState } from '../InspectionCreationWizard';

const mod = 'sections.DamageMechanisms';

/**
 * Number of internal steps this section contains.
 */
export const steps = 1;
export const label = `${mod}.summary-label`;

/**
 * Header generator.
 *
 * @param t translation function
 * @param state the current section state
 * @param focused if is the current section
 * @param asset selected asset
 * @param plan asset plan
 * @returns header title and subtitle
 */
export const header = (t: TFunction<string[]>, state: SectionState, focused: boolean, asset?: Asset, plan?: Plan) => ({
    title: t(`${mod}.header-title${!focused ? '-small' : ''}`),
    subtitle: t(`${mod}.header-subtitle${!focused ? '-small' : ''}`, { count: 0 }),
});

const useComponentClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        margin: '0 -10px',
    },
    contentTitle: {
        ...theme.typography.headings.h5,
    },
    contentSubtitle: {
        color: theme.colors.neutral[10],
        marginBottom: 16,
        ...theme.typography.content.caption.default,
    },
    title: {
        marginTop: 40,
        color: theme.colors.neutral[1],
        ...theme.typography.headings.h5,
    },
    subtitle: {
        color: theme.colors.neutral[1],
        ...theme.typography.content.caption.paragraph,
        marginBottom: 20,
        '& span': {
            color: theme.colors.blue[1],
            cursor: 'pointer',
        },
    },
    imageCard: {
        display: 'flex',
        padding: 10,
        borderRadius: 5,
        background: theme.colors.neutral[6],
        divShadow: '0 1px 1px 0 rgba(0, 0, 0, 0.14)',
        flexDirection: 'column',
    },
    image: {
        borderRadius: 5,
        width: '100%',
        maxHeight: 450,
        objectFit: 'cover',
        marginBottom: 10,
    },
    imageInfo: {
        display: 'flex',
        alignItems: 'center',
        flex: 1,
    },
    imageName: {
        flex: 1,
        ...theme.typography.content.label.default,
        color: theme.colors.neutral[2],
        ...theme.typography.content.label.default,
    },
    imageIcon: {
        color: theme.colors.neutral[2],
    },
    option: {
        padding: 10,
    },
});

/**
 * Damage mechanisms section of the inspection creation process.
 *
 * @param props.state the current section state
 * @param props.next function to advance to the next step
 */
export const Component = (props: { state: SectionState; next: () => void }) => {
    const classes = useComponentClasses();
    const { t, Trans } = usePrefixTranslation('inspection-creation', {
        'inspection-creation': 'sections.DamageMechanisms',
    });
    const dispatch = useTypedDispatch();
    const { plan, damageMechanism } = useTypedSelector(state => ({
        plan: state.plan.changed,
        damageMechanism: state.plan_damageMechanism,
    }));
    const uploadRef = React.useRef<() => Promise<void>>();

    const [pictures, setPictures] = React.useState<DisplayPicture[]>([]);
    React.useEffect(() => {
        dispatch(actions.plan_damageMechanism.get());
    }, []);

    React.useEffect(() => {
        const getPictures = async (blueprints: string[]) => {
            if (plan && blueprints.length !== 0) {
                const newBlueprints = await Promise.all(
                    blueprints.map(async blueprint => {
                        if (blueprint.startsWith('data:')) {
                            return {
                                title: blueprint,
                                url: blueprint,
                            };
                        }
                        const url = await getContentUrl(blueprint);
                        return {
                            title: blueprint,
                            url: url,
                        };
                    }),
                );

                const urls = newBlueprints.map(blueprint => {
                    return blueprint.url;
                });
                if (urls.includes('')) {
                    dispatch(actions.event.create('error', 'Error retrieving pictures'));
                }
                setPictures(newBlueprints.filter(blueprint => blueprint.url !== ''));
            }
        };
        if (plan) {
            getPictures(plan.blueprint_damages ?? []);
        }
    }, [plan?.blueprint_damages]);

    if (!plan) return <span>{'#plan not in store'}</span>;

    return (
        <div className={classes.root}>
            {props.state.editable && (
                <>
                    <span className={classes.contentTitle}>{t('content-title')}</span>
                    <span className={classes.contentSubtitle}>{t('content-subtitle')}</span>
                    <SearchBar
                        label={t('search-bar-label')}
                        loading={damageMechanism.fetching}
                        options={Object.values(damageMechanism.items)}
                        renderLoadingSpinner
                        renderEmpty={t('search-bar-empty')}
                        renderOption={option => (
                            <span className={classes.option}>{`${option.code}: ${option.name}`}</span>
                        )}
                        renderLabel={option => `${option.code}: ${option.name}`}
                        loadingIgnoreOptions
                        onSelect={option => {
                            if (!option) return;
                            const damageMechanism: DamageMechanism = {
                                id: -1,
                                type: 'internal',
                                probability: 'low',
                                description: '',
                                damage_descriptor: option,
                                inspection_plan: -1,
                            };
                            const damageMechanisms = [...(plan.damage_mechanism ?? []), damageMechanism];
                            dispatch(actions.plan.edit({ damage_mechanism: damageMechanisms }));
                        }}
                    />
                </>
            )}
            {(plan.damage_mechanism ?? []).map((dm, i) => (
                <DamageMechanismCard
                    key={i}
                    damageMechanism={dm}
                    onDelete={() => {
                        const damageMechanisms = [...(plan.damage_mechanism ?? [])];
                        damageMechanisms.splice(i, 1);
                        dispatch(actions.plan.edit({ damage_mechanism: damageMechanisms }));
                    }}
                    onPropertyChange={property => {
                        const damageMechanisms = [...(plan.damage_mechanism ?? [])];
                        damageMechanisms[i] = { ...damageMechanisms[i], [property.name]: property.value };
                        dispatch(
                            actions.plan.edit({
                                damage_mechanism: damageMechanisms,
                            }),
                        );
                    }}
                />
            ))}
            {props.state.editable && (
                <>
                    <span className={classes.title}>{t('title')}</span>
                    <span className={classes.subtitle}>
                        <Trans
                            i18nKey={'subtitle'}
                            components={{ choose: <span onClick={async () => uploadRef.current?.()} /> }}
                        />
                    </span>
                </>
            )}
            <PictureUploader
                pictures={pictures}
                limit={5}
                editable={props.state.editable}
                onChange={blueprint_damages => dispatch(actions.plan.edit({ blueprint_damages }))}
                uploadRef={uploadRef}
            />
        </div>
    );
};

const useDamageMechanismCardClasses = makeStyles({
    root: {
        display: 'flex',
        // justifyContent: 'space-between',
        borderRadius: 5,
        boxShadow: '0 1px 1px 0 rgba(0, 0, 0, 0.14)',
        margin: '5px 0',
    },
    code: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexBasis: 44,
        minWidth: 44,
        borderRadius: 2,
        color: theme.colors.neutral[1],
        ...theme.typography.content.label.default,
        '&.unset, &.low, &.moderate-low': {
            background: theme.colors.blue[3],
        },
        '&.moderate': {
            background: theme.colors.amber[4],
        },
        '&.moderate-high, &.high': {
            background: theme.colors.red[3],
        },
    },
    labels: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flex: 1,
        padding: '10px 20px',
        '& > :nth-child(2)': {
            color: theme.colors.neutral[2],
            ...theme.typography.content.caption.paragraph,
            textTransform: 'capitalize',
        },
        '& > :first-child': {
            color: theme.colors.darkBlue[1],
            ...theme.typography.headings.h5,
            textTransform: 'capitalize',
        },
    },
    actions: {
        display: 'flex',
        justifyContent: 'flex-end',
        flex: 1,
    },
    icon: {
        alignSelf: 'center',
        color: theme.colors.neutral[2],
    },
});

/**
 * Damage mechanism card.
 *
 * @param props.damageMechanism the damage mechanism object
 * @param props.onDelete callback triggered when the trash icon is clicked
 */
const DamageMechanismCard = (props: {
    damageMechanism: DamageMechanism;
    onDelete?: (damageMechanism: DamageMechanism) => void;
    onPropertyChange?: (property: { name: keyof DamageMechanism; value: string | undefined }) => void;
}) => {
    const classes = useDamageMechanismCardClasses();
    const badgeType = (probability: 'low' | 'moderate-low' | 'moderate' | 'moderate-high' | 'high') =>
        enumToStatusType[probability ?? 'unset'];
    const status = { [props.damageMechanism.probability ?? 'low']: true };
    const inspectionTypeName = (selectedOption: typeof inspectionTypesCombined[number]) => {
        return selectedOption.split(' ').length > 1 ? 'combined' : selectedOption;
    };

    return (
        <div className={classes.root}>
            <div className={clsx(classes.code, status)}>
                <span>{props.damageMechanism.damage_descriptor?.code ?? 'N/A'}</span>
            </div>
            <Grid container className={classes.labels} spacing={6}>
                <Grid item xs={12} md={12} lg={6}>
                    {props.damageMechanism.damage_descriptor?.name ?? 'no descriptor'}
                </Grid>
                <Grid item xs={12} md={12} lg={6}>
                    {props.damageMechanism.description}
                </Grid>
            </Grid>
            <Grid container direction='row' alignItems='center' className={classes.actions} spacing={2}>
                <Grid item xs={12} md={12} lg={5}>
                    <Select
                        defaultValue={props.damageMechanism.probability ?? 'unset'}
                        options={probabilities}
                        renderOption={option => <Badge type={badgeType(option) as any}>{option}</Badge>}
                        onValue={option => props.onPropertyChange?.({ name: 'probability', value: option })}
                    />
                </Grid>
                <Grid item xs={12} md={12} lg={5}>
                    <Select
                        defaultValue={props.damageMechanism.type ?? 'unset'}
                        options={inspectionTypesCombined}
                        renderOption={option => (
                            <Badge type='neutral'>{option === 'combined' ? 'ext int' : option}</Badge>
                        )}
                        onValue={(option?: typeof inspectionTypesCombined[number]) =>
                            props.onPropertyChange?.({
                                name: 'type',
                                value: inspectionTypeName(option as 'internal' | 'external' | 'combined'),
                            })
                        }
                    />
                </Grid>
                <Grid item xs={2} md={2} lg={1}>
                    <IconButton
                        className={classes.icon}
                        size='small'
                        onClick={() => props.onDelete?.(props.damageMechanism)}
                    >
                        <Trash />
                    </IconButton>
                </Grid>
            </Grid>
        </div>
    );
};
